<template>
  <v-container fluid ma-0 pa-0 px-4>
    <Loading :isLoading="loading">
      <div class="fixed-header" :class="{ 'mobile': $vuetify.breakpoint.smAndDown }">
        <!-- if has summary fields add class 'with-summary' -->
        <v-layout align-center fill-height class="fixed-header-wrapper">
          <v-flex shrink class="text-truncate" pr-4>
            <v-tooltip color="secondary" bottom>
              <template v-slot:activator="{ on }">
                <h2 v-on="on" class="p-relative text-truncate">
                  {{ title }}
                </h2>
              </template>
              <span>{{ title }}</span>
            </v-tooltip>
            <h4 class="pt-1 sub-header ">Account Configuration</h4>
          </v-flex>
          <v-spacer></v-spacer>
        </v-layout>
      </div>
      <v-layout justify-start column class="mb-4 pt-4">
        <v-container pa-0 fluid class="elevation-2 rounded-3" style="background-color:white">
          <v-tabs v-model="accountModel" grow show-arrows color="white" slider-color="primary" ref="tabs">
            <v-tab ripple v-for="item in items" :key="item.name" @click="handleClick(item)">
              {{ item.title }}
            </v-tab>
            <v-tab-item v-for="item in items" :key="item.name + '-content'">
              <v-divider></v-divider>
              <v-container fluid>
                <v-layout row wrap>
                  <v-flex xs12 px-4 pt-3 pb-1 mb-0>
                    <component :is="item.component" :account="account" :features-save-version="featuresSaveVersion" v-on:update-account-data="handleUpdate" />
                  </v-flex>
                </v-layout>
              </v-container>
            </v-tab-item>
          </v-tabs>
        </v-container>
        <v-layout row fluid wrap>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="toggleReindexDialog(true)" class="mt-4 ml-0" dark>Reindex</v-btn>
          <v-btn :disabled="!hasChanged" @click="save()" class="mt-4 mb-0 mx-0" color="success" style="float:right">Save
            changes</v-btn>
          <v-dialog v-model="dialogSave" max-width="500">
            <v-card class="pa-4">
              <v-card-title class="pa-0 pb-2">
                <h2>You are about to save changes to the account.</h2>
              </v-card-title>
              <v-card-actions class="ma-0 pa-0">
                <v-layout fluid row wrap>
                  <v-flex xs12>
                    <v-checkbox v-model="skipSaveWarning" class="ma-0 pa-0" color="secondary"
                      style="margin-bottom: -5px !important"
                      label="I understand, don't show me this dialog again when saving."></v-checkbox>
                  </v-flex>
                  <v-btn @click="performSave()" color="success">Confirm Save</v-btn>
                  <v-btn @click="dialogSave = false" color="error">Cancel</v-btn>
                </v-layout>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-layout>
        <confirmation-dialog title="Reindex Account" :message="reindexDialogMessage" confirmButtonLabel="Yes"
          :showDialog="showReindexDialog" v-on:confirm="reindex()"
          v-on:cancel="toggleReindexDialog(false)"></confirmation-dialog>
      </v-layout>
    </Loading>
  </v-container>
</template>

<script>
import Loading from "@/components/shared/Loading.vue"
import Details from "@/components/accounts/account-config/AccountConfigDetails.vue"
import Chat from "@/components/accounts/account-config/AccountConfigChat.vue"
import Features from "@/components/accounts/account-config/AccountConfigFeatures.vue"
import Security from "@/components/accounts/account-config/AccountConfigSecurity.vue"
import Emails from "@/components/accounts/account-config/AccountConfigEmails.vue"
import Layout from "@/components/accounts/account-config/AccountConfigLayout.vue"
import Runtime from "@/components/accounts/account-config/AccountConfigRuntime.vue"
import ConfirmationDialog from "@/components/shared/ConfirmationDialog.vue"
import { logFeature } from "@/helpers/featureActivity"
import { mapState } from "vuex"

export default {
  name: "account-config",
  components: { Loading, Details, Features, Security, ConfirmationDialog },
  data: () => ({
    accountModel: 0,
    accountUpdates: {},
    skipSaveWarning: false,
    dialogSave: false,
    hasChanged: false,
    localUpdate: false,
    changed: [],
    featuresSaveVersion: 0,
    showReindexDialog: false
  }),
  computed: {
    ...mapState({
      account: state => state.currentUser.account,
      accountTab: state => state.cbroute.params.account_tab,
      isAppAdmin: state => !!(state.currentUser && state.currentUser.isAppAdmin)
    }),
    items() {
      const all = [
        { name: "details", title: "Details", component: Details },
        { name: "features", title: "Features", component: Features },
        { name: "security", title: "Security", component: Security },
        { name: "emails", title: "Emails", component: Emails },
        { name: "layout", title: "Layout", component: Layout },
        { name: "chat", title: "Chat", component: Chat },
        { name: "runtime", title: "Runtime", component: Runtime },
      ]
      if (this.isAppAdmin) {
        return all
      }
      return all.filter((item) => item.name !== "features")
    },
    loading() {
      return false
    },
    title() {
      if (this.account && this.account.title) {
        return this.account.title
      } else {
        return "Admin"
      }
    },
    reindexDialogMessage() {
      return `Are you sure you want to reindex ${this.account.nickname}?`
    }
  },
  watch: {
    accountTab() {
      this.localUpdate = false
      this.handleRouteChange(this.accountTab)
    }
  },
  created() {
    this.handleRouteChange(this.accountTab)
  },
  methods: {
    save() {
      if (this.skipSaveWarning) {
        this.performSave()
      } else {
        if (this.shouldUpdateExisting) {
          this.dialogSaveAll = true
        } else {
          this.dialogSave = true
        }
      }
    },
    async performSave() {
      this.dialogSaveAll = false
      this.dialogSave = false
      if (this.skipSaveWarning) {
        this.$store.dispatch("toggleSaveWarnings", this.skipSaveWarning)
      }
      const lifecycleChanged = this.changed.includes("lifeCycleCallbacks")
      const llmKeyChanged = this.changed.includes("changedLlmApiKey")
      const oidcChanged = this.changed.includes("changedOidcSso")
      const featureToggles = this.account.changedAccountFeatures || {}
      const lifecycleOff = featureToggles.lifecycle_callbacks === false
      if ((lifecycleChanged || llmKeyChanged) && !lifecycleOff) {
        logFeature("runtime-vault", "save", "Saving account runtime settings", {
          hasKeyUpdate: llmKeyChanged,
        })
        await this.$store.dispatch("setScriptEngineAttrs", {
          attrs: this.account.lifeCycleCallbacks,
          llmApiKey: llmKeyChanged ? this.account.changedLlmApiKey : undefined,
        })
      }
      const oidcOff = featureToggles.oidc === false
      if (oidcChanged && !oidcOff) {
        logFeature("oidc", "save", "Saving account OIDC SSO settings", {
          enabled: !!(this.account.changedOidcSso && this.account.changedOidcSso.enabled),
        })
        await this.$store.dispatch("setOidcSso", this.account.changedOidcSso)
      }
      this.changed.forEach(change => {
        switch (change) {
          case "lifeCycleCallbacks":
          case "changedLlmApiKey":
          case "changedOidcSso":
            break
          case "emailAccountChangedIndeces":
            this.$store.dispatch("storeEmailAccounts", this.account[change])
            break
          case "changedTimezone":
            this.$store.dispatch("setAccountTimezone", this.account[change])
            break
          case "changedDefaultTeamId":
            this.$store.dispatch("setDefaultTeam", this.account[change])
            break
          case "changedChatBriefing":
            this.$store.dispatch("setAccountChatBriefing", this.account[change])
            break
          case "changedChatReports":
            this.$store.dispatch("setAccountChatReports", this.account[change])
            break
          case "changedChatMemoryEnabled":
            this.$store.dispatch("setChatMemoryEnabled", this.account[change])
            break
          case "changedRefreshTokenExpiryHours":
            this.$store.dispatch("setAccountRefreshTokenExpiryHours", this.account[change])
            break
          case "changedAdminConsoleToken":
            this.$store.dispatch("setAccountAdminConsoleToken", this.account[change])
            break
          case "changedAccountFeatures":
          case "changedFeatureAttrs":
            break
        }
      })
      if (this.changed.includes("changedAccountFeatures") || this.changed.includes("changedFeatureAttrs")) {
        const entries = []
        const lifecycleSavedWithAttrs = !lifecycleOff && (lifecycleChanged || llmKeyChanged)
        const oidcSavedWithAttrs = oidcChanged && !oidcOff
        const featureAttrs = this.account.changedFeatureAttrs || {}
        Object.keys(featureToggles).forEach((code) => {
          if (code === "lifecycle_callbacks" && lifecycleSavedWithAttrs) {
            return
          }
          if (code === "oidc" && oidcSavedWithAttrs) {
            return
          }
          const entry = { code: code, enabled: !!featureToggles[code] }
          if (entry.enabled && featureAttrs[code]) {
            entry.attrs = featureAttrs[code]
          }
          entries.push(entry)
        })
        Object.keys(featureAttrs).forEach((code) => {
          if (featureToggles[code] === false) {
            return
          }
          const already = entries.find((entry) => entry.code === code)
          if (already) {
            if (already.enabled) {
              already.attrs = featureAttrs[code]
            }
            return
          }
          entries.push({ code: code, enabled: true, attrs: featureAttrs[code] })
        })
        if (entries.length) {
          await this.$store.dispatch("setAccountFeatures", entries)
        }
        this.featuresSaveVersion += 1
      }
      this.hasChanged = false
    },
    handleUpdate(data) {
      if (data) {
        if (data.key !== "changedLlmApiKey" && data.key !== "changedOidcSso") {
          console.log("Account has changed", data.key)
        }
        this.hasChanged = true
        if (!this.changed.includes(data.key)) {
          this.changed.push(data.key)
        }
        this.account[data.key] = data.value
      }
    },
    handleClick(item) {
      this.localUpdate = true
      this.$router.push({ name: "AccountConfig", params: { account_tab: item.name } })
    },
    handleRouteChange(route) {
      if (this.localUpdate) {
        return
      }
      const name = route ? String(route).toLowerCase() : "details"
      if (name === "features" && !this.isAppAdmin) {
        this.accountModel = 0
        return
      }
      const idx = this.items.findIndex((item) => item.name === name)
      this.accountModel = idx >= 0 ? idx : 0
      this.localUpdate = false
    },
    toggleReindexDialog(state) {
      this.showReindexDialog = state
    },
    async reindex() {
      await this.$store.dispatch('performReindex')
      this.toggleReindexDialog(false)
    }
  }
}
</script>
